import React, { useEffect, useState, useRef, useCallback } from 'react'
import messages from './messages'
import { useIntl, FormattedMessage } from 'react-intl'
import { environment, adyenOriginKey, applePayMerchantId } from 'js/config'
import useLocale from '../../hooks/useLocale'
import AdyenCheckout from '@adyen/adyen-web'
import './adyen.scss'

const adyenLocales = {
  en: 'en_US',
  nl: 'nl_NL',
  sv: 'sv_SE',
  fr: 'fr_FR',
  de: 'de_DE',
  no: 'no_NO',
  ru: 'ru_RU',
  da: 'da_DK',
  it: 'it_IT',
  es: 'es_ES',
  pt: 'pt_BR',
}

export const paymentMethods = {
  card: {
    name: 'Bank card',
    type: 'scheme',
  },
  payPal: {
    configuration: {
      intent: 'capture',
    },
    name: 'PayPal',
    type: 'paypal',
  },
  googlepay: {
    name: 'Google Pay',
    type: 'googlepay',
  },
  applepay: {
    name: 'Apple Pay',
    type: 'applepay',
  },
}

const getPaymentMethods = (currency) => {
  const baseMethods = [
    paymentMethods.card,
    paymentMethods.payPal,
    paymentMethods.applepay,
    paymentMethods.googlepay,
  ]

  if (currency === 'INR') {
    return [paymentMethods.card]
  }

  return [...baseMethods]
}

type Props = {
  children: (props: {
    formProps: {
      ref: React.RefObject<HTMLFormElement>
      className: string
    }
    payPalSelected: boolean
    adyenCheckout: React.ReactNode
    selectedPaymentMethod: string | null
    consentChecked: boolean
    onConsentChange: (checked: boolean) => void
    showConsentCheckbox: boolean
  }) => React.ReactNode
  onSubmit: (state: any, dropin: any) => void
  scenario?: 'update-card'
  amountValue?: number
  plan?: number
  trialDuration?: number
}

function CreditCardForm({ ...props }: Props) {
  const { amountValue = 0, plan = 1, trialDuration = 0 } = props
  const [error, setError] = useState(false)
  const [payPalSelected, setPayPalSelected] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null)
  const [consentChecked, setConsentChecked] = useState(false)
  const form = useRef<HTMLFormElement>(null!)
  const { currency, language, country } = useLocale()
  const _intl = useIntl()

  const showConsentCheckbox =
    selectedPaymentMethod === 'googlepay' ||
    selectedPaymentMethod === 'applepay'

  const createApplePayRecurringConfig = () => {
    const baseConfig = {
      paymentDescription: 'Lifesum Membership Payment',
      managementURL: 'https://lifesum.com/account/subscription',
      tokenNotificationUrl: 'https://www.adyen.com/tokenNotificationUrl',
      regularBilling: {
        label: 'Subscription',
        amount:
          typeof amountValue === 'number'
            ? (amountValue / 100).toFixed(2)
            : '0.00',
        type: 'final' as const,
        paymentTiming: 'recurring' as const,
        recurringPaymentStartDate: new Date(),
        recurringPaymentIntervalUnit: 'month' as const,
        recurringPaymentIntervalCount: plan || 1,
      },
    }

    // TODO: Add end trial date
    if (trialDuration && trialDuration > 0) {
      return {
        ...baseConfig,
        trialBilling: {
          label: 'Free Trial',
          amount: '0.00',
          type: 'final' as const,
          paymentTiming: 'recurring' as const,
          recurringPaymentStartDate: new Date(),
          recurringPaymentIntervalUnit: 'day' as const,
          recurringPaymentIntervalCount: trialDuration,
        },
      }
    }

    return baseConfig
  }

  const configuration = {
    locale: adyenLocales[language] || 'en_US',
    clientKey: adyenOriginKey,
    environment: environment === 'production' ? 'live' : 'test',
    showPayButton: false,
    paymentMethodsConfiguration: {
      applepay: {
        amount: {
          currency,
          value: typeof amountValue === 'number' ? amountValue : 0,
        },
        countryCode: country || 'SE',

        recurringPaymentRequest: createApplePayRecurringConfig(),
        configuration: {
          merchantId: applePayMerchantId,
          merchantName: 'ShapeUpClubCOM',
          displayName: 'Lifesum',
        },
      },
      googlepay: {
        environment: environment === 'production' ? 'PRODUCTION' : 'TEST',
        amount: {
          currency,
          value: typeof amountValue === 'number' ? amountValue : 0,
        },
        countryCode: country || 'SE',
        configuration: {
          gatewayMerchantId: 'ShapeUpClubCOM',
          merchantId: 'BCR2DN7T6CU3DVIW',
          merchantName: 'Lifesum',
        },
      },
    },
    paymentMethodsResponse: {
      paymentMethods: getPaymentMethods(currency).map((method) => {
        return method
      }),
    },
    analytics: {
      enabled: false,
    },
  }

  const validateApplePayConfiguration = () => {
    const applePayConfig = configuration.paymentMethodsConfiguration?.applepay

    if (!applePayConfig) {
      return false
    }

    // Check essential fields for Apple Pay drop-in
    if (
      !applePayConfig.amount ||
      typeof applePayConfig.amount.value !== 'number'
    ) {
      return false
    }

    if (!applePayConfig.countryCode) {
      return false
    }

    return true
  }

  const loadAdyen = useCallback(async () => {
    setError(false)

    try {
      const checkout = await AdyenCheckout(configuration)

      const dropin = checkout.create('dropin', {
        onSubmit: (state, dropin) => {
          props.onSubmit(state, dropin)
        },
        onSelect: (component) => {
          const paymentType = component.props.type
          setPayPalSelected(paymentType === 'paypal')
          setSelectedPaymentMethod(paymentType)

          if (paymentType !== 'googlepay' && paymentType !== 'applepay') {
            setConsentChecked(false)
          }
        },
        onError: (error) => {
          console.error('Adyen payment error:', error)
          setError(true)
        },
        // Apple Pay specific event handlers for recurring payments
        onAuthorized: (_data, actions) => {
          // For recurring payments, this is where you'd handle the token
          actions.resolve()
        },
        onClick: (resolve, reject) => {
          // Validate Apple Pay configuration before proceeding
          const isValid = validateApplePayConfiguration()

          if (isValid) {
            resolve()
          } else {
            reject(new Error('Apple Pay configuration validation failed'))
          }
        },
      })

      dropin.mount('#adyen-credit-card-form')

      form.current.addEventListener('submit', (e) => {
        e.preventDefault()
        if (
          dropin.data?.['paymentMethod']?.type === paymentMethods.payPal.type
        ) {
          props.onSubmit(
            { data: { paymentMethod: { type: paymentMethods.payPal.type } } },
            dropin,
          )
        } else {
          dropin.submit()
        }
      })
    } catch (error) {
      setError(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadAdyen()
  }, [currency, amountValue, plan, trialDuration, loadAdyen]) // Added dependencies

  const handleConsentChange = (checked: boolean) => {
    setConsentChecked(checked)
  }

  if (error) {
    return (
      <div className="mb-24 rounded-8 border-2 border-secondary-red px-24 py-32">
        <h2 className="mb-12 font-demibold text-secondary-red">
          <FormattedMessage {...messages.pay_load_error_heading} />
        </h2>
        <p>
          <FormattedMessage {...messages.pay_load_error_text} />
        </p>
        <button
          className="mt-24 rounded-full bg-white px-24 py-4 text-secondary-blue type-14-demibold-caps"
          type="button"
          onClick={() => {
            loadAdyen()
          }}
        >
          <FormattedMessage {...messages.pay_try_again} />
        </button>
      </div>
    )
  }

  return props.children({
    formProps: {
      ref: form,
      className: 'c-pay__form',
    },
    payPalSelected: payPalSelected,
    adyenCheckout: (
      <div className="c-pay__fields-wrapper">
        <div id="adyen-credit-card-form" className="adyen-checkout" />
      </div>
    ),
    selectedPaymentMethod,
    consentChecked,
    onConsentChange: handleConsentChange,
    showConsentCheckbox,
  })
}

export default CreditCardForm

export const AcceptedPaymentMethods = () => (
  <picture>
    <source
      srcSet={`${require('media/payment-methods.png')}, ${require('media/payment-methods@2x.png')} 2x`}
    />
    <img
      className="mx-auto mt-24 block"
      src={require('media/payment-methods.png')}
      alt="We accept Visa, MasterCard, American Express, PayPal, Google Pay and Apple Pay"
      height="21"
      width="180"
    />
  </picture>
)

export const SecurePayments = () => (
  <p className="c-pay__secure mb-12">
    <FormattedMessage {...messages.securePayments} />
  </p>
)
